import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  auth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  handleMfaRequired
} from '../firebase';
import { showMfaModal } from '../components/landing';
import CookieConsent from '../components/CookieConsent';

// Static background - simplified on mobile for performance
const GradientBackground = () => {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {/* Base warm gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #fffbf7 0%, #fff7f0 25%, #fef3eb 50%, #fdf0e6 75%, #fcede1 100%)',
        }}
      />

      {/* Subtle dot pattern - hidden on mobile */}
      <div
        className="absolute inset-0 opacity-[0.4] hidden sm:block"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(249,115,22,0.15) 1px, transparent 0)`,
          backgroundSize: '24px 24px',
        }}
      />

      {/* Blobs - only on desktop (blur is expensive on mobile) */}
      <div className="hidden sm:block">
        <div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(249,115,22,0.4) 0%, rgba(249,115,22,0.1) 40%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute top-1/3 -left-48 w-[400px] h-[600px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(ellipse, rgba(251,146,60,0.5) 0%, rgba(249,115,22,0.2) 40%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div
          className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-25"
          style={{
            background: 'radial-gradient(ellipse, rgba(249,115,22,0.35) 0%, rgba(234,88,12,0.1) 50%, transparent 70%)',
            filter: 'blur(100px)',
          }}
        />
        <div
          className="absolute top-2/3 right-1/4 w-[300px] h-[300px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, rgba(251,146,60,0.6) 0%, transparent 60%)',
            filter: 'blur(50px)',
          }}
        />
      </div>

      {/* Simple mobile gradient accent */}
      <div
        className="sm:hidden absolute top-0 right-0 w-64 h-64 opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(249,115,22,0.5) 0%, transparent 70%)',
        }}
      />
    </div>
  );
};

// Navigation with scroll-aware logo color
const Navigation = ({ onGetStarted, mobileMenuOpen, setMobileMenuOpen, isOverDark }) => (
  <motion.nav
    initial={{ y: -100 }}
    animate={{ y: 0 }}
    transition={{ duration: 0.6, ease: "easeOut" }}
    className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-6"
  >
    <div className="max-w-6xl mx-auto flex items-center justify-between">
      <Link to="/" className={`text-base sm:text-xl font-light tracking-[0.25em] sm:tracking-[0.3em] transition-colors duration-300 ${
        isOverDark ? 'text-white' : 'text-gray-900'
      }`}>
        K L O R A H
      </Link>

      <div className="hidden md:flex items-center gap-1 px-2 py-2 rounded-full backdrop-blur-2xl border border-orange-200/50 shadow-lg"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.7) 100%)',
          boxShadow: '0 8px 32px rgba(249,115,22,0.15), 0 2px 8px rgba(249,115,22,0.08), inset 0 1px 0 rgba(255,255,255,0.9)'
        }}
      >
        <a href="#features" className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors rounded-full hover:bg-white/30">
          Features
        </a>
        <a href="#klo" className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors rounded-full hover:bg-white/30">
          Meet Klo
        </a>
        <a href="#pricing" className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors rounded-full hover:bg-white/30">
          Pricing
        </a>
        <button
          onClick={onGetStarted}
          className="px-5 py-2 rounded-full text-sm font-medium bg-[#f97316] text-white hover:bg-[#ea580c] transition-all"
        >
          Get Started
        </button>
      </div>

      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl bg-white/80 border border-gray-200/60"
      >
        <div className="flex flex-col gap-1.5">
          <span
            className={`block h-[2px] bg-gray-700 rounded-full transition-transform duration-200 origin-center ${mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`}
            style={{ width: 18 }}
          />
          <span
            className={`block h-[2px] w-3 bg-gray-700 rounded-full transition-opacity duration-200 ${mobileMenuOpen ? 'opacity-0' : ''}`}
          />
          <span
            className={`block h-[2px] bg-gray-700 rounded-full transition-transform duration-200 origin-center ${mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}
            style={{ width: 18 }}
          />
        </div>
      </button>
    </div>
  </motion.nav>
);

// Mobile Menu - optimized for performance
const MobileMenu = ({ isOpen, onClose, onGetStarted }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-40 md:hidden"
      >
        <div
          className="absolute inset-0"
          style={{ background: 'rgba(255,251,247,0.95)' }}
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-x-4 top-20 rounded-2xl border border-orange-100 p-5 shadow-xl bg-white"
        >
          <div className="flex flex-col space-y-1">
            <a
              href="#features"
              onClick={onClose}
              className="px-4 py-3 text-base font-medium text-gray-700 rounded-xl active:bg-gray-100"
            >
              Features
            </a>
            <a
              href="#klo"
              onClick={onClose}
              className="px-4 py-3 text-base font-medium text-gray-700 rounded-xl active:bg-gray-100"
            >
              Meet Klo
            </a>
            <a
              href="#pricing"
              onClick={onClose}
              className="px-4 py-3 text-base font-medium text-gray-700 rounded-xl active:bg-gray-100"
            >
              Pricing
            </a>
            <div className="pt-3">
              <button
                onClick={() => { onGetStarted(); onClose(); }}
                className="w-full text-center px-5 py-3.5 rounded-xl bg-[#f97316] text-white font-medium"
              >
                Get Started
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Auth Modal
const AuthModal = ({ isOpen, onClose, isSignUp, setIsSignUp, onGoogleSignIn, onEmailAuth, error, loading }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-3 sm:p-4">
      <motion.div
        className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-10 w-full max-w-[420px] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.25)] border border-white/50 relative overflow-hidden max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9, y: 40 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 sm:top-6 right-4 sm:right-6 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors group"
        >
          <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6 sm:mb-10">
          <motion.h2
            key={isSignUp ? 'signup' : 'signin'}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl sm:text-3xl font-light tracking-tight text-gray-900 mb-1 sm:mb-2"
          >
            {isSignUp ? 'Create your account' : 'Welcome back'}
          </motion.h2>
          <p className="text-xs sm:text-sm text-gray-500">
            {isSignUp ? 'Get started with Klorah' : 'Sign in to continue to Klorah'}
          </p>
        </div>

        {/* Google button */}
        <motion.button
          onClick={onGoogleSignIn}
          disabled={loading}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="w-full bg-white text-gray-700 border border-gray-200 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-medium flex items-center justify-center gap-2 sm:gap-3 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all mb-6 sm:mb-8 group text-sm sm:text-base"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          <span className="group-hover:tracking-wide transition-all duration-300">
            {loading ? 'Connecting...' : 'Continue with Google'}
          </span>
        </motion.button>

        {/* Divider */}
        <div className="relative flex items-center mb-5 sm:mb-8">
          <div className="flex-grow h-px bg-gray-200"></div>
          <span className="flex-shrink mx-4 sm:mx-6 text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 font-medium">or</span>
          <div className="flex-grow h-px bg-gray-200"></div>
        </div>

        {/* Form */}
        <form onSubmit={(e) => { e.preventDefault(); onEmailAuth(email, password); }} className="space-y-4 sm:space-y-5">
          {/* Email field */}
          <div className="relative">
            <motion.label
              className={`absolute left-3 sm:left-4 transition-all duration-200 pointer-events-none ${
                emailFocused || email
                  ? 'top-1.5 sm:top-2 text-[9px] sm:text-[10px] uppercase tracking-wider text-[#f97316] font-medium'
                  : 'top-1/2 -translate-y-1/2 text-xs sm:text-sm text-gray-400'
              }`}
            >
              Email
            </motion.label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              className={`w-full px-3 sm:px-4 pt-5 sm:pt-6 pb-2 sm:pb-3 bg-gray-50 text-gray-900 border-2 rounded-xl sm:rounded-2xl transition-all duration-200 outline-none text-sm sm:text-base ${
                emailFocused ? 'border-[#f97316] bg-white shadow-sm' : 'border-transparent hover:border-gray-200'
              }`}
              required
            />
          </div>

          {/* Password field */}
          <div className="relative">
            <motion.label
              className={`absolute left-3 sm:left-4 transition-all duration-200 pointer-events-none ${
                passwordFocused || password
                  ? 'top-1.5 sm:top-2 text-[9px] sm:text-[10px] uppercase tracking-wider text-[#f97316] font-medium'
                  : 'top-1/2 -translate-y-1/2 text-xs sm:text-sm text-gray-400'
              }`}
            >
              Password
            </motion.label>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              className={`w-full px-3 sm:px-4 pt-5 sm:pt-6 pb-2 sm:pb-3 pr-10 sm:pr-12 bg-gray-50 text-gray-900 border-2 rounded-xl sm:rounded-2xl transition-all duration-200 outline-none text-sm sm:text-base ${
                passwordFocused ? 'border-[#f97316] bg-white shadow-sm' : 'border-transparent hover:border-gray-200'
              }`}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>

          {/* Error message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Submit button */}
          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white py-3 sm:py-4 rounded-xl sm:rounded-2xl font-medium transition-all relative overflow-hidden group mt-1 sm:mt-2"
          >
            <span className="relative z-10 uppercase tracking-wider text-xs sm:text-sm">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing
                </span>
              ) : isSignUp ? 'Create account' : 'Sign in'}
            </span>
          </motion.button>
        </form>

        {/* Sign-up / Sign-in toggle */}
        <p className="text-center text-xs sm:text-sm text-gray-500 mt-4 sm:mt-6">
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-[#f97316] hover:text-[#ea580c] font-medium transition-colors"
          >
            {isSignUp ? 'Sign in' : 'Sign up'}
          </button>
        </p>
      </motion.div>
    </div>
  );
};

// Hero Demo - Wide interactive showcase (Invoicing)
const HeroDemo = () => {
  const [phase, setPhase] = useState(3); // Start at final state
  const [isMobile, setIsMobile] = useState(true); // Default to mobile (no animation)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    // Skip animation on mobile - show final state immediately
    if (isMobile) {
      setPhase(3);
      return;
    }

    let timeouts = [];
    const runAnimation = () => {
      setPhase(0);
      timeouts.push(setTimeout(() => setPhase(1), 1000));
      timeouts.push(setTimeout(() => setPhase(2), 2000));
      timeouts.push(setTimeout(() => setPhase(3), 3000));
      timeouts.push(setTimeout(() => runAnimation(), 7000));
    };

    runAnimation();
    return () => timeouts.forEach(t => clearTimeout(t));
  }, [isMobile]);

  return (
    <div className="w-[92%] sm:w-[85%] lg:w-[80%] mx-auto">
      <motion.div
        className="bg-white sm:bg-white/90 sm:backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-orange-100/60 overflow-hidden"
        style={{
          boxShadow: '0 25px 60px -12px rgba(249, 115, 22, 0.25), 0 12px 30px -8px rgba(249, 115, 22, 0.15), 0 0 0 1px rgba(249,115,22,0.05)',
        }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#f97316] to-[#c2410c] flex items-center justify-center">
            <span className="text-white text-xs sm:text-sm font-semibold">K</span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Klo</p>
            <p className="text-[10px] sm:text-xs text-gray-500">Your AI assistant</p>
          </div>
          <div className="ml-auto hidden sm:flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-gray-200" />
            <div className="w-3 h-3 rounded-full bg-gray-200" />
            <div className="w-3 h-3 rounded-full bg-gray-200" />
          </div>
        </div>

        {/* Chat area - responsive height */}
        <div className="p-4 sm:p-6 h-[320px] sm:h-[280px] relative">
          {/* User message */}
          <motion.div
            className="absolute top-4 sm:top-6 right-4 sm:right-6 left-4 sm:left-6 flex justify-end"
            initial={false}
            animate={{ opacity: phase >= 1 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="bg-[#f97316] text-white rounded-2xl rounded-br-md px-3 sm:px-4 py-2 sm:py-2.5 max-w-[90%] sm:max-w-[80%]">
              <p className="text-xs sm:text-sm">Invoice Tyler for the engagement shoot, $850</p>
            </div>
          </motion.div>

          {/* Klo typing indicator */}
          <motion.div
            className="absolute top-16 sm:top-20 left-4 sm:left-6 flex items-start gap-2 sm:gap-3"
            initial={false}
            animate={{ opacity: phase === 2 ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-[#f97316] to-[#c2410c] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-[10px] sm:text-xs font-medium">K</span>
            </div>
            <div className="flex gap-1.5 pt-2 sm:pt-3">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#f97316]/60"
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                />
              ))}
            </div>
          </motion.div>

          {/* Klo response with invoice */}
          <motion.div
            className="absolute top-16 sm:top-20 left-4 sm:left-6 right-4 sm:right-6 flex items-start gap-2 sm:gap-3"
            initial={false}
            animate={{ opacity: phase >= 3 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-[#f97316] to-[#c2410c] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-[10px] sm:text-xs font-medium">K</span>
            </div>
            <div className="space-y-2 sm:space-y-3 flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-700">
                Done! Here's Tyler's invoice ready to send:
              </p>

              {/* Invoice card */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 sm:p-4 border border-gray-200 max-w-xs">
                <div className="flex justify-between items-start mb-3 sm:mb-4">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm sm:text-base truncate">Tyler Morrison</p>
                    <p className="text-[10px] sm:text-xs text-gray-500">Engagement Photography</p>
                  </div>
                  <span className="text-base sm:text-lg font-semibold text-gray-900 ml-2">$850</span>
                </div>

                <div className="flex gap-2">
                  <button className="flex-1 py-2 sm:py-2.5 bg-[#f97316] text-white text-xs sm:text-sm rounded-lg font-medium flex items-center justify-center gap-1 sm:gap-2 hover:bg-[#ea580c] transition-colors">
                    <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="hidden xs:inline">Send</span> Invoice
                  </button>
                  <button className="px-3 sm:px-4 py-2 sm:py-2.5 text-gray-600 text-xs sm:text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Input area */}
        <div className="border-t border-gray-100 p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 bg-gray-50 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3">
            <span className="text-gray-400 text-xs sm:text-sm flex-1">Ask Klo anything...</span>
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#f97316] flex items-center justify-center">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Animated Scheduling Demo
const SchedulingDemo = () => {
  const [phase, setPhase] = useState(2); // Start at final state
  const [isMobile, setIsMobile] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setPhase(2);
      return;
    }

    let timeouts = [];
    const runAnimation = () => {
      setPhase(0);
      timeouts.push(setTimeout(() => setPhase(1), 800));
      timeouts.push(setTimeout(() => setPhase(2), 1800));
      timeouts.push(setTimeout(() => runAnimation(), 6000));
    };

    runAnimation();
    return () => timeouts.forEach(t => clearTimeout(t));
  }, [isMobile]);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white sm:bg-white/95 sm:backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-orange-100/50 overflow-hidden"
        style={{
          boxShadow: '0 25px 60px -12px rgba(249, 115, 22, 0.2), 0 12px 30px -8px rgba(249, 115, 22, 0.12), 0 0 0 1px rgba(249,115,22,0.04)',
        }}
      >
        <div className="p-5 sm:p-6 min-h-[400px] sm:min-h-[420px] relative">
          {/* User message */}
          <motion.div
            className="flex justify-end mb-5"
            initial={false}
            animate={{ opacity: phase >= 1 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="bg-orange-50 rounded-2xl rounded-br-md px-4 py-3 max-w-[85%]">
              <p className="text-sm text-gray-900">Schedule a call with Emma about her wedding</p>
            </div>
          </motion.div>

          {/* Agent response */}
          <motion.div
            className="flex items-start gap-3 mb-4"
            initial={false}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#f97316] to-[#c2410c] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">K</span>
            </div>
            <p className="text-sm text-gray-700 pt-1.5">I found a time that works for both of you:</p>
          </motion.div>

          {/* Calendar card */}
          <motion.div
            className="ml-11 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200"
            initial={false}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#f97316]/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="1.5">
                  <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">Wedding Consultation</p>
                <p className="text-xs text-gray-500 mt-0.5">with Emma Wilson</p>
              </div>
            </div>

            <div className="space-y-2.5 mb-4">
              <div className="flex items-center gap-2.5 text-sm text-gray-600">
                <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Tomorrow, 2:00 PM - 2:30 PM
              </div>
              <div className="flex items-center gap-2.5 text-sm text-gray-600">
                <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Video call
              </div>
            </div>

            <div className="flex gap-2">
              <button className="flex-1 py-2.5 bg-[#f97316] text-white text-sm rounded-lg font-medium hover:bg-[#ea580c] transition-colors">
                Send Invite
              </button>
              <button className="px-4 py-2.5 text-gray-600 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                Edit
              </button>
            </div>
          </motion.div>
        </div>

        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3">
            <span className="flex-1 text-sm text-gray-400">Ask Klo anything...</span>
            <div className="w-8 h-8 rounded-lg bg-[#f97316] flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Animated Contract Demo
const ContractDemo = () => {
  const [phase, setPhase] = useState(2);
  const [isMobile, setIsMobile] = useState(true);
  const checkItems = ['8 hours coverage', '$1,500 deposit required', 'Cancellation policy included'];

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setPhase(2);
      return;
    }

    let timeouts = [];
    const runAnimation = () => {
      setPhase(0);
      timeouts.push(setTimeout(() => setPhase(1), 800));
      timeouts.push(setTimeout(() => setPhase(2), 1800));
      timeouts.push(setTimeout(() => runAnimation(), 6000));
    };

    runAnimation();
    return () => timeouts.forEach(t => clearTimeout(t));
  }, [isMobile]);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white sm:bg-white/95 sm:backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-orange-100/50 overflow-hidden"
        style={{
          boxShadow: '0 25px 60px -12px rgba(249, 115, 22, 0.2), 0 12px 30px -8px rgba(249, 115, 22, 0.12), 0 0 0 1px rgba(249,115,22,0.04)',
        }}
      >
        <div className="p-5 sm:p-6 min-h-[420px] sm:min-h-[440px] relative">
          {/* User message */}
          <motion.div
            className="flex justify-end mb-5"
            initial={false}
            animate={{ opacity: phase >= 1 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="bg-orange-50 rounded-2xl rounded-br-md px-4 py-3 max-w-[85%]">
              <p className="text-sm text-gray-900">Create a contract for Sarah's wedding, June 15th</p>
            </div>
          </motion.div>

          {/* Agent response */}
          <motion.div
            className="flex items-start gap-3 mb-4"
            initial={false}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#f97316] to-[#c2410c] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">K</span>
            </div>
            <p className="text-sm text-gray-700 pt-1.5">Contract ready with your standard terms:</p>
          </motion.div>

          {/* Contract card */}
          <motion.div
            className="ml-11 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200"
            initial={false}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#f97316]/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="1.5">
                  <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">Wedding Contract</p>
                <p className="text-xs text-gray-500 mt-0.5">Sarah & James - June 15</p>
              </div>
            </div>
            <div className="space-y-3 mb-5 text-sm text-gray-600">
              {checkItems.map((item, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8L6.5 11.5L13 5" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button className="flex-1 py-2.5 bg-[#f97316] text-white text-sm rounded-lg font-medium hover:bg-[#ea580c] transition-colors">
                Send to Sarah
              </button>
              <button className="px-4 py-2.5 text-gray-600 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                Preview
              </button>
            </div>
          </motion.div>
        </div>

        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3">
            <span className="flex-1 text-sm text-gray-400">Ask Klo anything...</span>
            <div className="w-8 h-8 rounded-lg bg-[#f97316] flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Animated Follow-up Demo
const FollowUpDemo = () => {
  const [phase, setPhase] = useState(2);
  const [isMobile, setIsMobile] = useState(true);

  const leads = [
    { name: 'Emma W.', type: 'Wedding inquiry', initials: 'EW' },
    { name: 'David K.', type: 'Portrait session', initials: 'DK' },
    { name: 'Lisa M.', type: 'Family photos', initials: 'LM' },
  ];

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setPhase(2);
      return;
    }

    let timeouts = [];
    const runAnimation = () => {
      setPhase(0);
      timeouts.push(setTimeout(() => setPhase(1), 800));
      timeouts.push(setTimeout(() => setPhase(2), 1800));
      timeouts.push(setTimeout(() => runAnimation(), 6000));
    };

    runAnimation();
    return () => timeouts.forEach(t => clearTimeout(t));
  }, [isMobile]);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white sm:bg-white/95 sm:backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-orange-100/50 overflow-hidden"
        style={{
          boxShadow: '0 25px 60px -12px rgba(249, 115, 22, 0.2), 0 12px 30px -8px rgba(249, 115, 22, 0.12), 0 0 0 1px rgba(249,115,22,0.04)',
        }}
      >
        <div className="p-5 sm:p-6 min-h-[380px] sm:min-h-[400px] relative">
          {/* User message */}
          <motion.div
            className="flex justify-end mb-5"
            initial={false}
            animate={{ opacity: phase >= 1 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="bg-orange-50 rounded-2xl rounded-br-md px-4 py-3 max-w-[85%]">
              <p className="text-sm text-gray-900">Follow up with my leads from this week</p>
            </div>
          </motion.div>

          {/* Agent response */}
          <motion.div
            className="flex items-start gap-3 mb-4"
            initial={false}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#f97316] to-[#c2410c] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">K</span>
            </div>
            <p className="text-sm text-gray-700 pt-1.5">Here are personalized follow-ups for your 3 leads:</p>
          </motion.div>

          {/* Leads list */}
          <motion.div
            className="ml-11 space-y-3"
            initial={false}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
          >
            {leads.map((lead, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3.5 border border-gray-200 flex items-center justify-between"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
                    {lead.initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{lead.name}</p>
                    <p className="text-xs text-gray-500 truncate">{lead.type}</p>
                  </div>
                </div>
                <button className="px-3.5 py-2 text-xs rounded-lg font-medium bg-[#f97316] text-white hover:bg-[#ea580c] transition-colors flex-shrink-0 ml-2">
                  Send
                </button>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3">
            <span className="flex-1 text-sm text-gray-400">Ask Klo anything...</span>
            <div className="w-8 h-8 rounded-lg bg-[#f97316] flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Full-page Feature Section
const FeatureSection = ({ id, label, title, subtitle, children, isReversed }) => (
  <section id={id} className="min-h-[auto] sm:min-h-screen flex items-center py-16 sm:py-20 px-5 sm:px-6 relative z-10">
    {/* Section accent blob - hidden on mobile for performance */}
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden hidden sm:block"
      style={{ opacity: 0.5 }}
    >
      <div
        className={`absolute ${isReversed ? '-left-64' : '-right-64'} top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full`}
        style={{
          background: 'radial-gradient(circle, rgba(249,115,22,0.15) 0%, transparent 60%)',
          filter: 'blur(80px)',
        }}
      />
    </div>
    <div className="max-w-6xl mx-auto w-full">
      <div className={`grid lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-20 items-center`}>
        <motion.div
          className={`${isReversed ? 'lg:order-2' : ''} text-center lg:text-left`}
          initial={{ opacity: 0, x: isReversed ? 40 : -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-gradient-to-r from-orange-100 to-orange-50 text-[#f97316] rounded-full text-[11px] sm:text-sm font-medium mb-4 sm:mb-6 shadow-sm border border-orange-100">
            {label}
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.1] mb-4 sm:mb-6 text-gray-900">
            {title}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-500 leading-relaxed max-w-lg mx-auto lg:mx-0">
            {subtitle}
          </p>
        </motion.div>

        <motion.div
          className={isReversed ? 'lg:order-1' : ''}
          initial={{ opacity: 0, x: isReversed ? -40 : 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {children}
        </motion.div>
      </div>
    </div>
  </section>
);

// Voice Wave Animation Component - static on mobile for performance
const VoiceWave = () => {
  const [isMobile, setIsMobile] = useState(false);
  const bars = 5;
  const staticHeights = [12, 20, 16, 22, 14];

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <div className="flex items-center justify-center gap-0.5 sm:gap-1 h-8 sm:h-12">
      {[...Array(bars)].map((_, i) => (
        isMobile ? (
          <div
            key={i}
            className="w-1 bg-white rounded-full"
            style={{ height: staticHeights[i] }}
          />
        ) : (
          <motion.div
            key={i}
            className="w-1.5 bg-white rounded-full"
            animate={{
              height: ["8px", "20px", "14px", "26px", "10px", "8px"],
            }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              delay: i * 0.15,
              ease: "easeInOut",
            }}
          />
        )
      ))}
    </div>
  );
};

// Meet Klo Section
const MeetKloSection = () => {
  const [isMobile, setIsMobile] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
  <section id="klo" className="min-h-[auto] sm:min-h-screen flex items-center py-16 sm:py-20 px-5 sm:px-6 relative z-10">
    {/* Central glow effect - hidden on mobile for performance */}
    <div className="absolute inset-0 pointer-events-none overflow-hidden hidden sm:block">
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 50%)',
          filter: 'blur(60px)',
        }}
      />
    </div>
    <div className="max-w-4xl mx-auto text-center px-2">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        {/* Static on mobile, animated on desktop */}
        {isMobile ? (
          <div
            className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#f97316] to-[#c2410c] flex items-center justify-center"
            style={{ boxShadow: '0 25px 50px -12px rgba(249, 115, 22, 0.3)' }}
          >
            <VoiceWave />
          </div>
        ) : (
          <motion.div
            className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-[#f97316] to-[#c2410c] flex items-center justify-center shadow-2xl shadow-[#f97316]/20"
            animate={{
              boxShadow: [
                "0 25px 50px -12px rgba(249, 115, 22, 0.2)",
                "0 25px 50px -12px rgba(249, 115, 22, 0.4)",
                "0 25px 50px -12px rgba(249, 115, 22, 0.2)"
              ]
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <VoiceWave />
          </motion.div>
        )}

        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight mb-4 sm:mb-6 text-gray-900">
          Meet <span className="text-[#f97316]">Klo</span>
        </h2>

        <p className="text-base sm:text-lg md:text-xl text-gray-500 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
          Your AI assistant who actually understands photography business. Just tell her what you need, and she handles the rest.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto mt-8 sm:mt-12">
          {[
            { title: 'Natural conversation', desc: 'Talk like you would to an assistant' },
            { title: 'Learns your style', desc: 'Adapts to how you run your business' },
            { title: 'Always available', desc: 'Works around the clock for you' },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-white sm:bg-white/80 sm:backdrop-blur-sm border border-orange-100/50"
              style={{
                boxShadow: '0 10px 40px -10px rgba(249, 115, 22, 0.15), 0 4px 12px -4px rgba(249, 115, 22, 0.08)',
              }}
            >
              <h3 className="font-medium text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">{item.title}</h3>
              <p className="text-xs sm:text-sm text-gray-500">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-8 sm:mt-12"
        >
          <div className="inline-flex items-center px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-gray-50 border border-gray-200">
            <p className="text-xs sm:text-sm text-gray-600">
              <span className="font-medium text-gray-900">500+ photographers</span> already use Klo
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
  );
};

// Footer
const Footer = () => (
  <footer className="relative z-10 mt-16 sm:mt-32">
    {/* Aurora background */}
    <div className="relative min-h-[400px] sm:min-h-[500px] overflow-hidden">
      {/* Transparent base */}
      <div className="absolute inset-0 bg-transparent" />

      {/* Animated aurora waves - simplified on mobile */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(249,115,22,0.15) 50%, rgba(249,115,22,0.4) 100%)',
        }}
      />
      <motion.div
        className="absolute -top-1/2 -left-1/4 w-[150%] h-[200%] hidden sm:block"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(249,115,22,0.3) 0%, transparent 50%)',
          filter: 'blur(60px)',
        }}
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-1/4 -right-1/4 w-[100%] h-[150%] hidden sm:block"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(180,60,100,0.25) 0%, transparent 50%)',
          filter: 'blur(80px)',
        }}
        animate={{
          x: [0, -80, 0],
          y: [0, -30, 0],
          scale: [1.1, 1, 1.1],
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      {/* Simplified mobile glow */}
      <div
        className="absolute inset-0 sm:hidden"
        style={{
          background: 'radial-gradient(ellipse at center bottom, rgba(249,115,22,0.25) 0%, transparent 70%)',
        }}
      />

      {/* Noise overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Content */}
      <div className="relative px-4 sm:px-6 md:px-8 pt-12 sm:pt-20 pb-8 sm:pb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          {/* Main CTA area */}
          <div className="text-center mb-12 sm:mb-20">
            <motion.h2
              className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white mb-4 sm:mb-6 tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
            >
              Start creating<br/>
              <span className="bg-gradient-to-r from-white via-white/90 to-white/60 bg-clip-text text-transparent">with Klo today</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mt-6 sm:mt-10"
            >
              <Link
                to="/signup"
                className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-gray-900 rounded-full font-medium text-sm hover:bg-white/90 transition-all flex items-center justify-center gap-2"
              >
                Get started free
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <a
                href="#features"
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-white/70 hover:text-white rounded-full font-medium text-sm transition-all border border-white/10 hover:border-white/30 backdrop-blur-sm text-center"
              >
                See how it works
              </a>
            </motion.div>
          </div>

          {/* Glass card with links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-[#111] sm:bg-white/[0.03] sm:backdrop-blur-2xl border border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-10"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-12">
              {/* Logo & socials */}
              <div className="col-span-2 md:col-span-1">
                <Link to="/" className="text-lg sm:text-xl font-light tracking-[0.25em] text-white inline-block mb-3 sm:mb-4">
                  KLORAH
                </Link>
                <div className="flex items-center gap-2 mt-3 sm:mt-4">
                  <a href="https://x.com/klorahapp" target="_blank" rel="noopener noreferrer" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </a>
                  <a href="https://www.instagram.com/klorahapp/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a href="mailto:hello@klorah.com" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Links */}
              <div>
                <h3 className="text-[9px] sm:text-[10px] font-medium tracking-[0.2em] uppercase mb-3 sm:mb-4 text-white/30">Product</h3>
                <ul className="space-y-2 sm:space-y-2.5">
                  <li><a href="#features" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Features</a></li>
                  <li><a href="#pricing" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Pricing</a></li>
                  <li><a href="#klo" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Meet Klo</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-[9px] sm:text-[10px] font-medium tracking-[0.2em] uppercase mb-3 sm:mb-4 text-white/30">Company</h3>
                <ul className="space-y-2 sm:space-y-2.5">
                  <li><Link to="/privacy" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Privacy</Link></li>
                  <li><Link to="/terms" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Terms</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-[9px] sm:text-[10px] font-medium tracking-[0.2em] uppercase mb-3 sm:mb-4 text-white/30">Get Started</h3>
                <ul className="space-y-2 sm:space-y-2.5">
                  <li><Link to="/signup" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Sign Up</Link></li>
                  <li><Link to="/app" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Log In</Link></li>
                </ul>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="mt-6 sm:mt-10 pt-4 sm:pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-4">
              <p className="text-[10px] sm:text-xs text-white/30">© {new Date().getFullYear()} Klorah, Inc. All rights reserved.</p>
              <p className="text-[10px] sm:text-xs text-white/20">Built for photographers, by photographers.</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </footer>
);

// Main Landing Page
export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMonthly, setShowMonthly] = useState(false);
  const [isOverDark, setIsOverDark] = useState(false);

  const navigate = useNavigate();

  // Force light mode on landing page
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  // Scroll detection for logo color change over dark footer
  useEffect(() => {
    const handleScroll = () => {
      const footer = document.querySelector('footer');
      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;
        setIsOverDark(footerTop < 80);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const isNewUser = result.user?.metadata?.creationTime === result.user?.metadata?.lastSignInTime;
      if (isNewUser) {
        sessionStorage.setItem('isNewUser', 'true');
        navigate('/app/onboarding');
      } else {
        navigate('/app');
      }
    } catch (err) {
      setError('Failed to sign in: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (email, password) => {
    try {
      setLoading(true);
      setError('');
      if (isSignUp) {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(result.user);
        sessionStorage.setItem('isNewUser', 'true');
        navigate('/app/onboarding');
      } else {
        try {
          await signInWithEmailAndPassword(auth, email, password);
          navigate('/app');
        } catch (err) {
          if (err.code === 'auth/multi-factor-auth-required') {
            const mfaData = await handleMfaRequired(err);
            if (mfaData?.verificationId && mfaData?.resolver) {
              await showMfaModal(mfaData.verificationId, mfaData.resolver);
              navigate('/app');
              return;
            }
          }
          throw err;
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="text-gray-900 relative overflow-hidden min-h-screen">
      <GradientBackground />

      <Navigation
        onGetStarted={() => { setIsSignUp(true); setShowAuthModal(true); }}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        isOverDark={isOverDark}
      />

      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onGetStarted={() => { setIsSignUp(true); setShowAuthModal(true); }}
      />

      {/* Hero */}
      <section className="min-h-[90vh] sm:min-h-screen pt-28 sm:pt-32 md:pt-40 relative z-10">
        <div className="max-w-4xl mx-auto text-center px-6 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-[2.75rem] sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.08] mb-5 sm:mb-6 text-gray-900">
              Just say what<br/>
              <span className="text-[#f97316]">you need.</span>
            </h1>
            <p className="text-gray-500 text-[15px] sm:text-lg md:text-xl max-w-md mx-auto leading-relaxed mb-10 sm:mb-10">
              Klo is your AI assistant that handles invoices, contracts, and client follow-ups. Just ask.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <motion.button
                onClick={() => { setIsSignUp(true); setShowAuthModal(true); }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-[#f97316] text-white rounded-full text-sm sm:text-base font-medium hover:bg-[#ea580c] transition-all"
                style={{
                  boxShadow: '0 10px 40px -10px rgba(249, 115, 22, 0.5), 0 4px 12px -2px rgba(249, 115, 22, 0.3)',
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Get started free
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8H13M13 8L8.5 3.5M13 8L8.5 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.button>
            </div>
          </motion.div>
        </div>

        <div className="mt-12 sm:mt-12 md:mt-16">
          <HeroDemo />
        </div>
      </section>

      {/* Features */}
      <div id="features">
        <FeatureSection
          label="SCHEDULING"
          title="Book calls in seconds."
          subtitle="Tell Klo who you need to meet. She finds a time that works, creates the invite, and sends it."
        >
          <SchedulingDemo />
        </FeatureSection>

        <FeatureSection
          label="CONTRACTS"
          title="Contracts in seconds."
          subtitle="Describe the shoot. Klo generates a contract with your terms, sends it for signature, and collects the deposit."
          isReversed
        >
          <ContractDemo />
        </FeatureSection>

        <FeatureSection
          label="FOLLOW-UPS"
          title="Never lose a lead."
          subtitle="Ask Klo to follow up with your leads. She drafts personalized messages based on each conversation."
        >
          <FollowUpDemo />
        </FeatureSection>
      </div>

      {/* Meet Klo */}
      <MeetKloSection />

      {/* Pricing */}
      <section id="pricing" className="min-h-[auto] sm:min-h-screen flex items-center py-16 sm:py-20 px-5 sm:px-6 relative z-10"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(249,115,22,0.03) 50%, rgba(249,115,22,0.06) 100%)',
        }}
      >
        <div className="max-w-5xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-3 sm:mb-4 text-gray-900">Simple pricing</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-500 mb-6 sm:mb-8">Start free. Upgrade when ready.</p>

            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white border border-gray-200">
              <button
                onClick={() => setShowMonthly(false)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${!showMonthly ? 'bg-[#f97316] text-white' : 'text-gray-600'}`}
              >
                Yearly
              </button>
              <button
                onClick={() => setShowMonthly(true)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${showMonthly ? 'bg-[#f97316] text-white' : 'text-gray-600'}`}
              >
                Monthly
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-md mx-auto"
          >
            <div className="bg-white sm:bg-white/95 sm:backdrop-blur-sm border border-orange-100/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 relative overflow-hidden"
              style={{
                boxShadow: '0 30px 60px -12px rgba(249, 115, 22, 0.25), 0 18px 36px -18px rgba(249, 115, 22, 0.2), 0 0 0 1px rgba(249,115,22,0.05)',
              }}
            >
              {!showMonthly && (
                <div className="absolute top-0 right-0 bg-[#f97316] text-white text-[10px] sm:text-xs px-3 sm:px-4 py-1 sm:py-1.5 rounded-bl-xl font-medium">
                  SAVE $60/YEAR
                </div>
              )}
              <h3 className="text-lg sm:text-xl font-medium mb-1 sm:mb-2 text-gray-900">Pro</h3>
              <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">{showMonthly ? 'Cancel anytime' : 'Billed annually'}</p>
              <div className="mb-4 sm:mb-6">
                <span className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900">${showMonthly ? '25' : '20'}</span>
                <span className="text-gray-500 ml-2 text-sm sm:text-base">/month</span>
              </div>
              <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 text-gray-600 text-sm sm:text-base">
                {[
                  'Unlimited conversations with Klo',
                  'Invoice creation & tracking',
                  'Contract generation',
                  'Client follow-ups',
                  'Payment processing',
                  ...(showMonthly ? [] : ['2 months free'])
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 sm:gap-3">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10L8 14L16 6" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <motion.button
                onClick={() => { setIsSignUp(true); setShowAuthModal(true); }}
                className="block w-full py-3 sm:py-3.5 text-center bg-[#f97316] text-white rounded-full font-medium hover:bg-[#ea580c] transition-colors text-sm sm:text-base"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Start free trial
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="min-h-[40vh] sm:min-h-[60vh] flex items-center justify-center px-5 sm:px-6 py-16 sm:py-0 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight mb-8 sm:mb-10 text-gray-900">
              Ready to meet<br/><span className="text-[#f97316]">Klo?</span>
            </h2>
            <motion.button
              onClick={() => { setIsSignUp(true); setShowAuthModal(true); }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-[#f97316] text-white rounded-full text-base sm:text-lg font-medium hover:bg-[#ea580c] transition-all"
              style={{
                boxShadow: '0 10px 40px -10px rgba(249, 115, 22, 0.5), 0 4px 12px -2px rgba(249, 115, 22, 0.3)',
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Get started free
              <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="none">
                <path d="M4 10H16M16 10L10.5 4.5M16 10L10.5 15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.button>
          </motion.div>
        </div>
      </section>

      <Footer />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        isSignUp={isSignUp}
        setIsSignUp={setIsSignUp}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailAuth={handleEmailAuth}
        error={error}
        loading={loading}
      />

      <CookieConsent />
    </div>
  );
}
